// Clinical constants. Values sourced from published literature
// (see Implementation Plan §1 for citations). Surfaced as estimates
// in UI; never used to suggest a dose to the user.

// Approximate 7-hydroxymitragynine content per gram of leaf kratom.
// Real values vary by source (0.3-0.5 mg/g typical). 0.4 is the midpoint.
export const MG_7OH_PER_GRAM_LEAF = 0.4;

// Approximate mitragynine content per gram of leaf kratom (the dominant
// alkaloid by mass). Published assays of dried leaf cluster in the
// 10-20 mg/g range (about 1-2% of dry weight); 14 is the midpoint.
// Used by the optional MEQ (mitragynine-equivalent) tracking surface
// in Insights — the unit some users prefer because it reflects what
// they're actually consuming rather than the downstream 7-OH metabolite.
// User-overridable via UserSettings.mitragyninePerGramLeaf.
export const MG_MITRAGYNINE_PER_GRAM_LEAF = 14;

// Approximate mitragynine-equivalent for 1 mg of pure 7-OH. Derived
// from the in-vivo 7-OH:mitragynine serum ratio of 0.20-0.31 reported
// in Hanapi et al., 2024: about 25% of absorbed mitragynine shows up
// as 7-OH, so the reverse is ~4× (1 mg of pure 7-OH represents the
// effect-side equivalent of ~4 mg of mitragynine being metabolized).
// Used to compute MEQ for tablet_7oh and extract doses where the
// alkaloid in question is 7-OH (synthesized/extracted) rather than
// the natural leaf path. NOT a pharmacology claim — it's a tracking-
// unit conversion the user can interpret as they choose.
export const MITRAGYNINE_PER_MG_7OH = 4;

// Effective-zero threshold for taper math. The final scheduled day is
// always forced to 0; the taper "reaches" zero when target <= this value.
export const EFFECTIVE_ZERO_MG = 0.5;

// Safety bands on per-week reduction rate.
export const TAPER_BAND = {
  GENTLE_MAX: 0.1, //  <= 10%/wk
  STANDARD_MAX: 0.25, //  10-25%/wk
  AGGRESSIVE_MAX: 0.4, //  25-40%/wk
  // > 0.4 = cold-turkey-equivalent
} as const;

// Crisis-flag thresholds for the adaptive guidance cron.
export const ADAPTIVE_TRIGGERS = {
  HOLD_SUGGEST_SOWS: 30, // SOWS >=30 two consecutive days
  HOLD_CONSECUTIVE_DAYS: 2,
  CLINICIAN_SUGGEST_SOWS: 40, // SOWS >=40 OR cravings >=8 ...
  CLINICIAN_SUGGEST_CRAVINGS: 8,
  CLINICIAN_CONSECUTIVE_DAYS: 5, // ... for >=5 consecutive days
  BASELINE_REASSESS_ADHERENCE: 0.6, // adherence <60% over 7 days
  BASELINE_REASSESS_WINDOW: 7,
} as const;

// Common 7-OH dose presets in mg. Historically these mirrored sold
// tablet strengths (5, 7, 7.5, 10, 12.5, 14, 15, 30) but most users
// take stacked doses well above any single tablet size; the chip
// strip is now the typical-dose surface, not a tablet catalog.
export const COMMON_TABLET_STRENGTHS_MG = [5, 10, 15, 20, 30, 50, 75, 100];

// Common adjunctive medications.
// NOTE: Loperamide is INTENTIONALLY OMITTED from the curated chip list.
// Per Part 12 §P12.4 A4 (addiction-medicine review): putting loperamide
// on a curated menu of withdrawal aids in an OTC-opioid-seeking
// population is entrapment for cardiotoxicity (QT/torsades at
// supra-therapeutic doses). Users who type "Loperamide" themselves
// still get the existing safety-warning card via adjunctMedSafety.ts;
// the chip just no longer hands it to them by default.
export const SUGGESTED_MEDS = [
  { name: 'Clonidine', schedule: 'PRN' },
  { name: 'Lofexidine', schedule: 'TID' },
  { name: 'Hydroxyzine', schedule: 'TID' },
  { name: 'Gabapentin', schedule: 'TID' },
  { name: 'Ondansetron', schedule: 'PRN' },
  { name: 'Melatonin', schedule: 'HS' },
  { name: 'Ibuprofen', schedule: 'PRN' },
  { name: 'Magnesium', schedule: 'HS' },
];

export const CRISIS_RESOURCES = {
  lifeline: { label: '988 Suicide & Crisis Lifeline', tel: '988' },
  text: { label: 'Crisis Text Line', sms: '741741', keyword: 'HOME' },
  samhsa: { label: 'SAMHSA National Helpline', tel: '18006624357' },
  emergency: { label: 'Emergency', tel: '911' },
} as const;
